/**
 * Calendar Routes Configuration
 *
 * This file contains all calendar-related route definitions extracted from the main router.
 * It provides better organization and maintainability for calendar-specific routing logic.
 */

import type { RouteRecordRaw } from 'vue-router'
import { br } from '@/plugins/trackerPlugin'
import { loadLocales } from '@/utils/i18n.utils'
import { useCommonStore } from '@/stores/CommonStore'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

// Calendar component imports
const CalendarLayout = () => import('@/layouts/CalendarLayout.vue')
const Calendar3Column = () => import('@/pages/calendar/Calendar3Column.vue')
const CalendarSettings = () => import('@/pages/calendar/CalendarSettings.vue')

/**
 * Get calendar route configuration
 */
export function getCalendarRoutes(): RouteRecordRaw {
  return {
    name: 'calendarLayout',
    path: 'calendar',
    alias: '',
    component: CalendarLayout,
    beforeEnter: async () => {
      await loadLocales('calendar')
    },
    props: true,
    meta: { trackingName: br.appActionFeature.calendar, section: 'calendar' },
    children: [
      {
        name: 'calendar3Column',
        path: '/calendar',
        component: Calendar3Column,
        props: true,
        meta: {
          requiresMatched: true,
          trackingName: br.appActionFeature.calendar
        }
      },
      {
        name: 'calendar3ColumnWithDate',
        path: '/calendar/:startDate',
        component: Calendar3Column,
        props: true,
        meta: {
          requiresMatched: true,
          layoutContainsAds: false,
          trackingName: br.appActionFeature.calendar
        }
      },
      {
        name: 'calendarSettings',
        path: '/calendar/settings',
        component: CalendarSettings,
        beforeEnter: async () => {
          const { globalFlagsLoaded, featureFlags } =
            storeToRefs(useCommonStore())

          if (!globalFlagsLoaded.value) {
            await new Promise<void>((resolve) => {
              const unwatch = watch(globalFlagsLoaded, (loaded) => {
                if (loaded) {
                  unwatch()
                  resolve()
                }
              })
            })
          }

          if (featureFlags.value?.web_calendar_connect) return true

          return { name: 'calendar3Column' }
        },
        props: true,
        meta: {
          requiresAuth: true,
          allowRefresh: true,
          trackingName: br.appActionFeature.calendar
        }
      }
    ]
  }
}
